import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { faShareNodes } from '@fortawesome/pro-solid-svg-icons/faShareNodes';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Button, Drawer, Group, Menu, Title } from '@mantine/core';
import { useIntl } from 'react-intl';

import { AdditionalDetailsSection } from './components/additional-details-section/AdditionalDetailsSection';
import { DocumentsSection } from './components/documents-section/DocumentsSection';
import { HandlingUnitSection } from './components/handling-unit-section/HandlingUnitSection';
import { NotesSection } from './components/notes-section/NotesSection';
import { ShipmentDetailsSection } from './components/shipment-details-section/ShipmentDetailsSection';
import { ShipmentSummarySection } from './components/shipment-summary-section/ShipmentSummarySection';
import { TrackingHistorySection } from './components/tracking-history-section/TrackingHistorySection';
import { freightDetailsMessages } from './freight-details-messages';
import { FreightStatusBadge } from './freight-status-badge/FreightStatusBadge';
import styles from './FreightDetails.module.css';

import type { FreightDetailsProps } from './freight-details-types';

/**
 * Top-level, store-agnostic Freight Details view. Owns the right-side Mantine
 * Drawer and composes the six detail sections into a two-column grid. All data
 * arrives via props and all side effects flow out through callbacks; the host
 * supplies `zIndex` for drawer stacking.
 */
export const FreightDetails = ({
  isOpen,
  shipment,
  originAddress,
  displayDimensionUnit,
  displayWeightUnit,
  currencyCode,
  trackingHistory,
  notes,
  zIndex,
  isDocumentsLoading,
  onPrintDocument,
  onDownloadDocument,
  onShareDocument,
  pendingDocumentTypes,
  onShare,
  onPrintAll,
  onShareAll,
  onDownloadAll,
  onTrack,
  onClose,
  onVoid,
  orderNumber,
  onOrderLinkClick,
  onGetHelp,
  amplitudeTrackingId,
}: FreightDetailsProps) => {
  const intl = useIntl();

  // Null bolNumber → fall back to the generic localized "Freight Details" title
  // rather than rendering "BOL #" with an empty number.
  const titleText =
    shipment.bolNumber !== null
      ? intl.formatMessage(freightDetailsMessages.bolTitle, { bolNumber: shipment.bolNumber })
      : intl.formatMessage(freightDetailsMessages.title);

  return (
    <Drawer
      classNames={{ body: styles.drawerBodySlot, content: styles.drawerContent, header: styles.header, title: styles.titleSlot }}
      onClose={onClose}
      opened={isOpen}
      padding={0}
      position='right'
      size='100%'
      title={
        <Group className={styles.titleBar} gap='xs' justify='space-between' wrap='nowrap'>
          <Title className={styles.titleText} data-amplitude-id={amplitudeTrackingId} order={3}>
            {titleText}
          </Title>
          <Group gap='xs' wrap='nowrap'>
            {onShare && (
              <Button leftSection={<FontAwesomeIcon icon={faShareNodes} />} onClick={() => onShare?.()} variant='ghost'>
                {intl.formatMessage(freightDetailsMessages.share)}
              </Button>
            )}
            {onPrintAll && (
              <Button
                data-testid='freight-details-print-all'
                leftSection={<FontAwesomeIcon icon={faPrint} />}
                onClick={() => onPrintAll?.()}
                variant='ghost'
              >
                {intl.formatMessage(freightDetailsMessages.print)}
              </Button>
            )}
            <ActionIcon
              aria-label={intl.formatMessage(freightDetailsMessages.closeAriaLabel)}
              color='gray'
              onClick={onClose}
              variant='ghost'
            >
              <FontAwesomeIcon icon={faXmark} />
            </ActionIcon>
          </Group>
        </Group>
      }
      withCloseButton={false}
      zIndex={zIndex}
    >
      <div className={styles.statusHeader}>
        <FreightStatusBadge shipment={shipment} />
        {orderNumber && onOrderLinkClick && (
          <Button onClick={onOrderLinkClick} variant='ghost'>
            {intl.formatMessage(freightDetailsMessages.orderLink, { orderNumber })}
          </Button>
        )}
        <Group gap='md'>
          {onGetHelp && (
            <Button onClick={onGetHelp} variant='tertiary'>
              {intl.formatMessage(freightDetailsMessages.getHelpButton)}
            </Button>
          )}
          <Menu position='bottom-end' width='target'>
            <Menu.Target>
              <Button variant='tertiary'>{intl.formatMessage(freightDetailsMessages.shipmentActions)}</Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item disabled={!onTrack} onClick={onTrack}>
                {intl.formatMessage(freightDetailsMessages.trackMenuItem)}
              </Menu.Item>
              <Menu.Item disabled={!onVoid} onClick={onVoid}>
                {intl.formatMessage(freightDetailsMessages.voidMenuItem)}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </div>

      <div className={styles.bodyGrid}>
        <div className={styles.column}>
          <ShipmentDetailsSection originAddress={originAddress} shipment={shipment} />
          <HandlingUnitSection displayDimensionUnit={displayDimensionUnit} displayWeightUnit={displayWeightUnit} shipment={shipment} />
          <AdditionalDetailsSection currencyCode={currencyCode} shipment={shipment} />
          <NotesSection notes={notes} />
        </div>
        <div className={styles.column}>
          <TrackingHistorySection
            carrierName={shipment.carrierName}
            onTrack={onTrack}
            shipment={shipment}
            trackingHistory={trackingHistory}
          />
          <DocumentsSection
            documents={shipment.documents}
            isDocumentsLoading={isDocumentsLoading}
            onDownloadAll={onDownloadAll}
            onDownloadDocument={onDownloadDocument}
            onPrintAll={onPrintAll}
            onPrintDocument={onPrintDocument}
            onShareAll={onShareAll}
            onShareDocument={onShareDocument}
            pendingDocumentTypes={pendingDocumentTypes}
          />
          <ShipmentSummarySection currencyCode={currencyCode} shipment={shipment} />
        </div>
      </div>
    </Drawer>
  );
};
